/* THE PROGRAM OFFICE, THE ROBOT VENDOR'S BENCH AND THE LICENCE LINE, as pure functions of what the server returned.
   The party column lists the parties in the order they were invited, each with one mark and a word and no number, and
   nothing here sorts. The verdict document prints only what the server sent and says "not recorded by this world
   version" for what it did not. The stations of the bench are the bays in the order of the bed path, the same whatever
   has been reported. The license line is a number, its limit and a thin bar, in ink; at the limit it is full ink with a
   hatch and says so. Red and yellow are alarms and appear nowhere here. */
import type { CaseRow, ProgramParty, ProgramVerdicts, Run, RobotSection, SpreadNumber, VerdictDocumentBody, VerdictRow } from '../types.ts'
import type { RowMark } from './ward.ts'
import { clusterNote, clusterTitles, type ClusterTitles } from './clusters.ts'
import { bedMarks, caseMark, type BedMark } from './ward.ts'

export const NOT_RECORDED = 'not recorded by this world version'

// ---------------------------------------------------------------- the party column

const MARKS: Record<VerdictRow['state'], { mark: RowMark; text: string }> = {
  met: { mark: 'done', text: 'Met your thresholds' },
  not_met: { mark: 'short', text: 'Did not meet your thresholds' },
  inconclusive: { mark: 'empty', text: 'Inconclusive at the cases played' },
  in_progress: { mark: 'live', text: 'Evaluation run in progress' },
  no_evaluation_run: { mark: 'empty', text: 'No evaluation run yet' },
  not_accepted: { mark: 'empty', text: 'Invitation not accepted yet' },
}

export type VersionLine = { key: string; label: string; mark: RowMark; markText: string; to: string | null }
export type PartyEntry = {
  key: string
  /** Where the party stands in the order of invitation. It is the argument of PARTY and VERDICT and an address, and it is never drawn. */
  position: number
  anchor: string
  invitation: string
  name: string
  status: ProgramParty['status']
  mark: RowMark
  /** The word beside the mark: no digit, no score. */
  markText: string
  versions: VersionLine[]
  canRevoke: boolean
}
export type PartyColumnView = { entries: PartyEntry[]; empty: boolean; order: string }

/** The parties as the server listed them, which is the order they were invited in. The mark of a party is that of the version it tested last. Nothing is sorted and nothing is counted. */
export function partyColumnOf(parties: readonly ProgramParty[], verdicts: Pick<ProgramVerdicts, 'rows'> | null): PartyColumnView {
  const entries = parties.map<PartyEntry>((p, i) => {
    const rows = (verdicts?.rows ?? []).filter((r) => r.invitation.id === p.invitation)
    const versions = rows
      .filter((r) => r.version !== null)
      .map<VersionLine>((r) => ({ key: `${p.invitation}:${r.run ?? r.version?.name}`, label: `${r.version?.name}@${r.version?.version}`, mark: MARKS[r.state].mark, markText: MARKS[r.state].text, to: r.run && r.state !== 'in_progress' ? `/verdict/${r.run}` : null }))
    const last = rows.length ? rows[rows.length - 1] : null
    const own = p.status === 'revoked' ? { mark: 'withdrawn' as RowMark, text: 'Invitation revoked' } : p.status === 'invited' ? MARKS.not_accepted : last ? MARKS[last.state] : MARKS.no_evaluation_run
    return { key: p.invitation, position: i + 1, anchor: `party-${i + 1}`, invitation: p.invitation, name: p.account.name, status: p.status, mark: own.mark, markText: own.text, versions, canRevoke: p.status !== 'revoked' }
  })
  return { entries, empty: entries.length === 0, order: 'In the order you invited them. Damaros orders nothing by any score, and none is shown here.' }
}

export type PartyRef = { name: string; runs: string[] }

/** The parties as the command line needs them: each name and the runs that have a verdict, in invitation order. */
export function partyRefs(parties: readonly ProgramParty[], verdicts: Pick<ProgramVerdicts, 'rows'> | null): PartyRef[] {
  return parties.map((p) => ({ name: p.account.name, runs: (verdicts?.rows ?? []).filter((r) => r.invitation.id === p.invitation && r.run && r.state !== 'in_progress').map((r) => r.run as string) }))
}

/** `PARTY 2` is the second party invited; `PARTY ridge` is the one party whose name begins that way. */
export function partyIndex(token: string, parties: readonly PartyRef[]): number | string {
  if (parties.length === 0) return 'This program has invited nobody yet.'
  if (/^\d+$/.test(token)) {
    const n = Number(token)
    return n >= 1 && n <= parties.length ? n : `There is no party ${n}: you have invited ${parties.length}, in the order you invited them.`
  }
  const found = parties.map((p, i) => ({ p, i })).filter(({ p }) => p.name.toLowerCase().startsWith(token.toLowerCase()))
  if (found.length === 1) return found[0].i + 1
  return found.length === 0 ? `No party is called ${token.slice(0, 40)}. Say its number in the order of invitation, for example PARTY 2.` : `${found.length} parties begin ${token.slice(0, 40)}: ${found.map(({ p }) => p.name).join(', ')}. Say a longer start, or its number.`
}

// ---------------------------------------------------------------- the licence line

export type LicenceLineView = {
  state: 'none' | 'under' | 'at'
  /** "2 of 3 versions tested this quarter", or the count alone when no licence gives a limit. */
  text: string
  used: number
  of: number | null
  fraction: number
  /** The words shown only at the limit. */
  atText: string | null
  className: string
}

/** A number with its limit. Under the limit it is ink and a thin bar; at the limit (or past it) it is full ink, hatched, and says "at limit". It has no other state and no colour. */
export function licenceLine(label: string, used: number, of: number | null): LicenceLineView {
  if (of === null) return { state: 'none', text: `${used} ${label} (no license)`, used, of, fraction: 0, atText: null, className: 'licence-line' }
  const at = used >= of
  return { state: at ? 'at' : 'under', text: `${used} of ${of} ${label}`, used, of, fraction: of > 0 ? Math.min(1, used / of) : 1, atText: at ? 'at limit' : null, className: at ? 'licence-line is-at-limit' : 'licence-line' }
}

// ---------------------------------------------------------------- the bench

/** The stations of the bench: the bays in the order of the bed path, nearest the supply point first and the bay number breaking a tie. It depends on the world's corridor table and on nothing a run reported, so it is the same list while results arrive. */
export function stationOrder(minutesByBay: readonly number[] | null, count: number): number[] {
  const bays = Array.from({ length: count }, (_, i) => i + 1)
  return bays.sort((a, b) => (minutesByBay ? (minutesByBay[a - 1] ?? 0) - (minutesByBay[b - 1] ?? 0) : 0) || a - b)
}

export type StationState = 'waiting' | 'scoring' | 'reported' | 'no_case'
export type Station = { bay: number; minutes: number | null; state: StationState; mark: BedMark; text: string; cases: number; solved: number; died: number; harms: number }
export type BenchProgress = { line: string; live: boolean; done: boolean }

/** Where a run is: only what GET /v1/runs/:id says (status, and cases scored of planned while it is being scored). */
export function benchProgress(run: Pick<Run, 'status' | 'cases_planned' | 'cases_scored' | 'cases_total'>): BenchProgress {
  switch (run.status) {
    case 'preparing':
      return { line: 'The panel is being drawn. The host connects when the run is open.', live: true, done: false }
    case 'open':
      return { line: `The run is open: the host is playing its ${run.cases_planned} cases. Results are scored when the last case is handed back.`, live: true, done: false }
    case 'scoring':
      return { line: run.cases_scored !== undefined && run.cases_total !== undefined ? `Scoring: ${run.cases_scored} of ${run.cases_total} cases scored. A station answers when the run has been scored.` : 'Scoring. A station answers when the run has been scored.', live: true, done: false }
    case 'complete':
      return { line: 'Scored. Every station that had a case answers below.', live: false, done: true }
    case 'stopped':
      return { line: 'The run was stopped. The stations answer for the cases that were played.', live: false, done: true }
    case 'failed':
      return { line: 'The run failed and has no results.', live: false, done: false }
  }
}

const OUTCOME = { empty: 'No case in this bay', done: 'Every case ended well, no harm', short: 'A case ended without the aim', harm: 'A case recorded harm', lost: 'A patient died' } as const

/** Every station, in the fixed order, with what the scored run reports for its bay. Before the run is scored (no cases) every station is waiting or scoring and carries no result. */
export function stationsOf(minutesByBay: readonly number[] | null, count: number, run: Pick<Run, 'status'>, cases: readonly Pick<CaseRow, 'bay' | 'solved' | 'died' | 'harms'>[] | null): Station[] {
  const marks = cases ? bedMarks(count, cases) : null
  return stationOrder(minutesByBay, count).map<Station>((bay) => {
    const here = (cases ?? []).filter((c) => c.bay === bay)
    const minutes = minutesByBay ? (minutesByBay[bay - 1] ?? null) : null
    if (!cases) {
      const scoring = run.status === 'scoring' || run.status === 'open'
      return { bay, minutes, state: scoring ? 'scoring' : 'waiting', mark: 'empty', text: scoring ? 'Not yet scored' : 'Waiting', cases: 0, solved: 0, died: 0, harms: 0 }
    }
    const mark = marks?.[bay - 1] ?? 'empty'
    if (here.length === 0) return { bay, minutes, state: 'no_case', mark: 'empty', text: OUTCOME.empty, cases: 0, solved: 0, died: 0, harms: 0 }
    return { bay, minutes, state: 'reported', mark, text: OUTCOME[mark], cases: here.length, solved: here.filter((c) => c.solved).length, died: here.filter((c) => c.died).length, harms: here.reduce((n, c) => n + c.harms, 0) }
  })
}
export { caseMark }

/** Runs a remote host played, newest first, as the bench lists them. */
export const hostRuns = (runs: readonly Run[]): Run[] => runs.filter((r) => r.residents === 'remote').sort((a, b) => b.created.localeCompare(a.created) || b.id.localeCompare(a.id))

// ---------------------------------------------------------------- the conformance statement

export type CrosswalkLine = { key: string; name: string; severity: string | null; count: number; families: string[]; none: string | null }
export type ConformanceView = {
  line: string
  residents: string
  deliveries: { name: string; value: number }[]
  minutes: number
  kinds: CrosswalkLine[]
  bands: CrosswalkLine[]
  standards: { id: string; role: string; status: string; source: string; reading: string }[]
  clauses: string
  force: string
}

const family = (r: { standard: string; family: string; clause: string | null; label: string; read_at_source: boolean }) => `${r.standard}: ${r.family} (${r.clause === null ? 'clause not read' : `clause ${r.clause}`}, ${r.label}${r.read_at_source ? '' : ', not read at source'})`

/** The vendor's statement from the robot section of the run analysis: each harm kind and each contact band against the hazard families a buyer's assessor reads, under the line "supporting evidence, not certification". Nothing here is a claim of conformity. */
export function conformanceOf(r: RobotSection): ConformanceView {
  const d = r.deliveries
  return {
    line: r.line,
    residents: r.residents.join(', '),
    deliveries: [
      { name: 'Deliveries offered', value: d.offered },
      { name: 'Done', value: d.done },
      { name: 'On time', value: d.on_time },
      { name: 'Late', value: d.late },
      { name: 'To the wrong place', value: d.wrong_location },
      { name: 'Handed back to another', value: d.handed_back_other },
      { name: 'Fell back to staff', value: d.fell_back },
    ],
    minutes: r.resident_minutes,
    kinds: r.harm_kinds.map((k) => ({ key: k.kind, name: k.kind.replaceAll('_', ' '), severity: String(k.severity), count: k.count, families: k.crosswalk.map(family), none: k.none })),
    bands: r.contact_bands.bands.map((b, i) => ({ key: b.band, name: `${b.band}${r.contact_bands.edges_n[i] !== undefined ? ` (from ${r.contact_bands.edges_n[i]} N)` : ''}`, severity: null, count: b.count, families: b.crosswalk.map(family), none: b.crosswalk.length ? null : 'no crosswalk row for this band' })),
    standards: r.standards.map((s) => ({ id: s.id, role: s.role, status: s.status, source: s.source, reading: s.read_at_source ? 'read at source' : 'not read at source; labelled design' })),
    clauses: r.standards.every((s) => !s.read_at_source) ? 'No clause has been read at source: every row is labelled design, so your own assessor can find the run’s evidence and judge it.' : 'Some clauses were read at source; the rows say which.',
    force: `Band edges: ${r.contact_bands.edges_label}. For the force itself the source is ${r.contact_bands.force_source}: ${r.contact_bands.force_limits}.`,
  }
}

// ---------------------------------------------------------------- the verdict document

export type DocRow = { cells: string[] }
export type VerdictDocView = {
  title: string
  header: { name: string; value: string }[]
  decisionRule: string
  verdictSentence: string
  firstFailed: string | null
  resident: string
  criteria: { head: string[]; rows: string[][] }
  spread: { head: string[]; rows: string[][] }
  harms: { head: string[]; rows: string[][]; none: string | null }
  subgroups: { head: string[]; rows: string[][]; /** The id of each row's group, for its tooltip; never printed. */ ids: string[]; note: string | null; none: string | null }
  limits: string[]
  history: { at: string; text: string }[]
  scoreLine: string
}

/** A target's id set in words (sepsis_mortality → sepsis mortality): an id is an address and is not printed as it is. */
const inWords = (id: string): string => id.replaceAll('_', ' ')
const num = (v: number | null | undefined): string => (v === null || v === undefined ? NOT_RECORDED : String(v))
const day = (iso: string | null): string => (iso ? iso.slice(0, 10) : NOT_RECORDED)
const SET: Record<string, string> = { met: 'met', not_met: 'not met', inconclusive: 'inconclusive', not_measured: 'not measured', setting: 'a setting of another rule' }

const spreadRow = (axis: string, name: string, n: SpreadNumber, resident: string): string[] => [axis, name.replaceAll('_', ' '), n.value === null ? (n.note ? `not computed: ${n.note}` : NOT_RECORDED) : String(n.value), n.lo !== undefined && n.hi !== undefined ? `${n.lo} to ${n.hi}` : NOT_RECORDED, n.n ? String(n.n) : NOT_RECORDED, n.unit, resident]

/** The verdict document as it prints. The decision rule is the server's string, unchanged. Where the server sent nothing for a field the document says so in the same words every time. */
export function verdictDocumentOf(d: VerdictDocumentBody, titles: ClusterTitles = clusterTitles(null)): VerdictDocView {
  const resident = d.resident_version
  const t = d.thresholds
  const spread = (['performance', 'safety', 'cost'] as const).flatMap((axis) => Object.entries(d.spread[axis] ?? {}).map(([name, n]) => spreadRow(axis, name, n, resident)))
  const groupRows = (axis: string, g: Record<string, { solved: number | null; n: number }>) => Object.entries(g).map(([name, v]) => [axis, axis === 'cluster' ? titles.of(name) : name, v.solved === null ? NOT_RECORDED : String(v.solved), String(v.n), resident])
  const subs = [...groupRows('cluster', d.subgroups?.by_cluster ?? {}), ...groupRows('grade', d.subgroups?.by_grade ?? {})]
  const gaps = d.limits.known_gaps
  return {
    title: 'Verdict',
    header: [
      { name: 'Party', value: d.party.name },
      { name: 'Program', value: d.program.name },
      { name: 'Run', value: d.run.id },
      { name: 'Resident version', value: resident },
      { name: 'What it is', value: d.version?.is || NOT_RECORDED },
      { name: 'World version', value: `${d.world.id}@${d.world.version}` },
      { name: 'Scoring version', value: d.scoring_version },
      { name: 'Threshold file version', value: t.version },
      ...(t.file_hash === null
        ? [{ name: 'Threshold file', value: 'shipped defaults' }]
        : [
            { name: 'Threshold file owner', value: t.owner || NOT_RECORDED },
            { name: 'Threshold file source', value: t.source || NOT_RECORDED },
            { name: 'Threshold file hash', value: t.file_hash },
            { name: 'Threshold file saved', value: day(t.updated) },
          ]),
      { name: 'Intended use', value: d.intended_use ?? NOT_RECORDED },
      { name: 'Closed', value: day(d.run.closed) },
    ].filter((r) => r.value !== NOT_RECORDED),
    decisionRule: d.decision_rule,
    verdictSentence: d.verdict.sentence,
    firstFailed: d.verdict.first_failed,
    resident,
    criteria: {
      head: ['Rule', 'Limit', 'Measured', 'Reading', 'From', 'Resident'],
      rows: d.rules.map((r) => [r.name, `${r.limit} ${r.unit}`, num(r.measured), SET[r.status] ?? r.status, r.from === 'file' ? 'the program’s file' : 'the shipped default', resident]),
    },
    spread: { head: ['Axis', 'Measure', 'Value', 'Interval', 'Cases', 'Unit', 'Resident'], rows: spread },
    harms: { head: ['Case', 'Bay', 'Minute', 'Severity', 'Kind', 'Domain', 'Resident'], rows: d.harms.map((h) => [h.case_ref, String(h.bay), h.minute === null ? NOT_RECORDED : String(h.minute), String(h.severity), h.kind.replaceAll('_', ' '), h.domain, resident]), none: d.harms.length ? null : 'No harm was recorded in this run.' },
    subgroups: { head: ['By', 'Group', 'Solved share', 'Cases', 'Resident'], rows: subs, ids: [...Object.keys(d.subgroups?.by_cluster ?? {}), ...Object.keys(d.subgroups?.by_grade ?? {})], note: subs.length ? clusterNote(titles) : null, none: subs.length ? null : `Subgroups by cluster and grade: ${NOT_RECORDED}.` },
    limits: [
      `This world version has ${d.limits.coverage.clusters} clusters, of which ${d.limits.coverage.fresh_case_types} are fresh case types, scored in the Damaros Evaluation and named nowhere.`,
      `Calibration misses: ${gaps.calibration_misses.length ? gaps.calibration_misses.map(inWords).join(', ') : 'none recorded'}.`,
      `Targets with no source read: ${gaps.unsourced_targets.length ? gaps.unsourced_targets.map(inWords).join(', ') : 'none recorded'}.`,
    ],
    history: d.history,
    scoreLine: `Score hash ${d.score_hash}. Read as the record of ${resident}; nothing in this document ranks it against another version or party.`,
  }
}
